import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, X, Trash2, Flag, UserPlus, MessageSquare, Send, Paperclip, Download, Loader2, File as FileIcon, UploadCloud, Lock, CheckSquare, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ItemMenu from "../../components/os/ItemMenu";
import ConnectedAreas from "../../components/os/ConnectedAreas";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const COLS = ["backlog", "todo", "in_progress", "review", "done"];
const LEADERSHIP = ["admin", "ceo", "director"];
const PRIO_CLS = { low: "bg-navy/10 text-navy/50", medium: "bg-violet/10 text-violet", high: "bg-gold/15 text-gold" };
const SPRINT_CLS = { planned: "bg-navy/10 text-navy/60", active: "bg-violet/10 text-violet", completed: "bg-emerald-500/10 text-emerald-600" };
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

const initialsOf = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const AV_COLORS = ["bg-violet", "bg-navy", "bg-emerald-500", "bg-gold", "bg-blue-600", "bg-rose-500"];
const avColor = (id) => AV_COLORS[(id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length];

function Avatar({ name, id, size = "w-7 h-7", title }) {
  return (
    <div title={title || name} className={`${size} ${avColor(id)} rounded-full flex items-center justify-center text-white text-[10px] font-medium shrink-0 ring-2 ring-white`}>
      {initialsOf(name)}
    </div>
  );
}

export default function OmniaFlowProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("board");
  const [modal, setModal] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [edit, setEdit] = useState(null);
  const saveEdit = async (e) => {
    e.preventDefault();
    try { await api.patch(`/flow/projects/${id}`, edit); setEdit(null); toast.success("Proyecto actualizado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };
  const deleteProject = async () => {
    if (!window.confirm("¿Eliminar este proyecto? Se borrarán sus tareas y archivos.")) return;
    try { await api.delete(`/flow/projects/${id}`); toast.success("Proyecto eliminado"); navigate("/os/flow"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };

  const load = useCallback(() => {
    api.get(`/flow/projects/${id}`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const moveTask = async (tid, status) => {
    setData((d) => ({ ...d, tasks: d.tasks.map((tk) => (tk.id === tid ? { ...tk, status } : tk)) }));
    try { await api.patch(`/flow/tasks/${tid}`, { status }); } catch (e) { toast.error("Error"); load(); }
  };
  const delTask = async (tid) => {
    await api.delete(`/flow/tasks/${tid}`); load();
  };

  if (!data) return <OSLayout title={t("os.nav.flow")}><div className="text-navy/40">{t("os.loading")}</div></OSLayout>;

  const { project, tasks, sprints, members = [] } = data;
  const TABS = ["board", "timeline", "sprints", "comments", "files"];
  const canManage = LEADERSHIP.includes(user?.role) || project.owner_id === user?.id;
  const memberById = Object.fromEntries(members.map((m) => [m.id, m]));
  const doneIds = new Set(tasks.filter((tk) => tk.status === "done").map((tk) => tk.id));

  return (
    <OSLayout title={project.name}>
      <Link to="/os/flow" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("os.flow.back")}
      </Link>

      <div className="space-y-6">
        <ConnectedAreas module="flow" entityId={id} />
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: project.color }} />
              <h2 className="font-editorial text-3xl tracking-tight">{project.name}</h2>
            </div>
            <p className="text-navy/50 mt-1">{project.description}</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="flow-add-task" onClick={() => setModal("task")}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
              <Plus className="w-4 h-4" /> {t("os.flow.task.add")}
            </button>
            {canManage && (
              <ItemMenu testId="flow-detail" onEdit={() => setEdit({ name: project.name, description: project.description || "" })} onDelete={deleteProject} />
            )}
          </div>
        </div>

        {/* Team strip */}
        <div className="flex items-center gap-3 flex-wrap" data-testid="flow-members-strip">
          <span className="overline text-navy/40">{t("os.flow.members.title")}</span>
          <div className="flex items-center -space-x-2">
            {members.map((m) => (
              <div key={m.id} className="relative group">
                <Avatar name={m.name} id={m.id} title={`${m.name} · ${m.role}`} />
                {canManage && m.id !== project.owner_id && (
                  <button data-testid={`flow-member-remove-${m.id}`} title={t("os.flow.members.remove")}
                    onClick={async () => { await api.delete(`/flow/projects/${id}/members/${m.id}`); toast.success(t("os.flow.members.removed")); load(); }}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-destructive text-white items-center justify-center hidden group-hover:flex z-10">
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {canManage && (
            <button data-testid="flow-invite-btn" onClick={() => setModal("invite")}
              className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors">
              <UserPlus className="w-3.5 h-3.5" /> {t("os.flow.members.invite")}
            </button>
          )}
        </div>

        <div className="flex gap-2 border-b border-navy/10 overflow-x-auto">
          {TABS.map((tk) => (
            <button key={tk} data-testid={`flow-tab-${tk}`} onClick={() => setTab(tk)}
              className={`px-4 py-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === tk ? "border-violet text-navy" : "border-transparent text-navy/50 hover:text-navy"}`}>
              {t(`os.flow.tabs.${tk}`)}
            </button>
          ))}
        </div>

        {/* Kanban */}
        {tab === "board" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="flow-board-panel">
            {COLS.map((col) => {
              const colTasks = tasks.filter((tk) => tk.status === col);
              return (
                <div key={col} data-testid={`flow-col-${col}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dragId && moveTask(dragId, col)}
                  className="bg-white/60 border border-navy/10 rounded-lg p-3 min-h-[120px]">
                  <div className="flex items-center justify-between px-1 mb-3">
                    <span className="overline text-navy/50">{t(`os.flow.cols.${col}`)}</span>
                    <span className="text-xs text-navy/30">{colTasks.length}</span>
                  </div>
                  <div className="space-y-2">
                    {colTasks.map((tk) => {
                      const asg = memberById[tk.assignee_id];
                      const cl = tk.checklist || [];
                      const clDone = cl.filter((c) => c.done).length;
                      const blocked = (tk.depends_on || []).some((d) => !doneIds.has(d));
                      return (
                        <motion.div key={tk.id} layout draggable
                          onDragStart={() => setDragId(tk.id)} onDragEnd={() => setDragId(null)}
                          onClick={() => setModal({ type: "detail", task: tk })}
                          data-testid={`flow-task-${tk.id}`}
                          className="group bg-white border border-navy/10 rounded-lg p-3 cursor-pointer hover:border-violet/40 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium leading-snug">{tk.title}</p>
                            <button data-testid={`flow-task-del-${tk.id}`} onClick={(e) => { e.stopPropagation(); delTask(tk.id); }}
                              className="opacity-0 group-hover:opacity-100 text-navy/30 hover:text-destructive transition-opacity shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ${PRIO_CLS[tk.priority]}`}><Flag className="w-2.5 h-2.5" />{t(`os.flow.priority.${tk.priority}`)}</span>
                            {blocked && <span className="text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 bg-gold/15 text-gold" title={t("os.flow.detail.blockedBadge")}><Lock className="w-2.5 h-2.5" /></span>}
                            {cl.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 bg-navy/5 text-navy/50"><CheckSquare className="w-2.5 h-2.5" />{clDone}/{cl.length}</span>}
                            {tk.due_date && <span className="text-[10px] text-navy/40">{tk.due_date}</span>}
                            <div className="ml-auto">
                              {asg ? <Avatar name={asg.name} id={asg.id} size="w-6 h-6" title={asg.name} />
                                : tk.assignee ? <span className="text-[10px] text-navy/40">{tk.assignee}</span> : null}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Timeline / Gantt */}
        {tab === "timeline" && <FlowTimeline tasks={tasks} memberById={memberById} t={t} />}

        {/* Sprints */}
        {tab === "sprints" && (
          <div className="space-y-4" data-testid="flow-sprints-panel">
            <div className="flex justify-end">
              <button data-testid="flow-add-sprint" onClick={() => setModal("sprint")}
                className="inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors">
                <Plus className="w-4 h-4" /> {t("os.flow.sprint.add")}
              </button>
            </div>
            {sprints.length === 0 ? (
              <div className="text-navy/40">{t("os.flow.sprint.empty")}</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {sprints.map((s) => {
                  const st = tasks.filter((tk) => tk.sprint_id === s.id);
                  const done = st.filter((tk) => tk.status === "done").length;
                  const prog = st.length ? Math.round((done / st.length) * 100) : 0;
                  return (
                    <div key={s.id} data-testid={`flow-sprint-${s.id}`} className="bg-white border border-navy/10 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <h3 className="text-lg font-medium">{s.name}</h3>
                        <select data-testid={`flow-sprint-status-${s.id}`} value={s.status}
                          onChange={async (e) => { await api.patch(`/flow/sprints/${s.id}`, { status: e.target.value }); load(); }}
                          className={`text-[11px] px-2 py-1 rounded-full outline-none ${SPRINT_CLS[s.status]}`}>
                          {["planned", "active", "completed"].map((v) => <option key={v} value={v}>{t(`os.flow.sprintStatus.${v}`)}</option>)}
                        </select>
                      </div>
                      {s.goal && <p className="text-sm text-navy/50 mt-1">{s.goal}</p>}
                      <div className="text-xs text-navy/40 mt-3">{s.start_date} → {s.end_date}</div>
                      <div className="mt-3 flex items-center gap-3">
                        <div className="flex-1 h-1.5 rounded-full bg-navy/5 overflow-hidden">
                          <div className="h-full bg-gradient-violet" style={{ width: `${prog}%` }} />
                        </div>
                        <span className="text-xs text-navy/40">{st.length} {t("os.flow.sprint.tasks")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Comment wall */}
        {tab === "comments" && <CommentWall projectId={id} user={user} t={t} />}

        {/* Files */}
        {tab === "files" && <FilesPanel projectId={id} user={user} t={t} />}
      </div>

      {modal === "task" && <TaskModal sprints={sprints} members={members} t={t} onClose={() => setModal(null)}
        onSubmit={async (f) => { await api.post("/flow/tasks", { ...f, project_id: id }); toast.success("OK"); setModal(null); load(); }} />}
      {modal === "sprint" && <SprintModal t={t} onClose={() => setModal(null)}
        onSubmit={async (f) => { await api.post("/flow/sprints", { ...f, project_id: id }); toast.success("OK"); setModal(null); load(); }} />}
      {modal === "invite" && <InviteModal projectId={id} members={members} t={t} onClose={() => setModal(null)} onDone={load} />}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={() => setEdit(null)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
            onClick={(e) => e.stopPropagation()} data-testid="flow-detail-edit" className="w-full max-w-md bg-cream rounded-xl p-8">
            <div className="flex items-center justify-between mb-6"><h3 className="text-xl font-medium">Editar proyecto</h3><button onClick={() => setEdit(null)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
            <form onSubmit={saveEdit} className="space-y-4">
              <input required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Nombre" className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              <textarea rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Descripción" className="w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors resize-none" />
              <button className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">Guardar cambios</button>
            </form>
          </motion.div>
        </div>
      )}
      {modal?.type === "detail" && (
        <TaskDetailModal task={modal.task} tasks={tasks} members={members} t={t}
          onClose={() => setModal(null)} onChanged={load} />
      )}
    </OSLayout>
  );
}

function CommentWall({ projectId, user, t }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/flow/comments`, { params: { project_id: projectId, task_id: "" } })
      .then((r) => setItems(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try { await api.post("/flow/comments", { project_id: projectId, task_id: "", body: text.trim() }); setText(""); load(); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error"); }
    finally { setSending(false); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="max-w-2xl space-y-4" data-testid="flow-comments-panel">
      <div className="flex items-center gap-2 text-navy/70">
        <MessageSquare className="w-4 h-4" /> <span className="font-medium">{t("os.flow.comments.title")}</span>
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input data-testid="flow-comment-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder={t("os.flow.comments.placeholder")} className={inputCls} />
        <button data-testid="flow-comment-send" disabled={sending}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-gradient-violet text-white text-sm px-4 disabled:opacity-50 hover:opacity-90 transition-opacity">
          <Send className="w-4 h-4" /> {t("os.flow.comments.send")}
        </button>
      </form>
      {items.length === 0 ? (
        <div className="text-navy/40 py-8" data-testid="flow-comments-empty">{t("os.flow.comments.empty")}</div>
      ) : (
        <div className="space-y-3">
          {items.slice().reverse().map((c) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease }}
              data-testid={`flow-comment-${c.id}`} className="flex gap-3 bg-white border border-navy/10 rounded-lg p-4">
              <Avatar name={c.author_name} id={c.author_id} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{c.author_name}</span>
                  <span className="text-[11px] text-navy/35">{fmt(c.created_at)}</span>
                </div>
                <p className="text-sm text-navy/70 mt-1 whitespace-pre-wrap break-words">{c.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilesPanel({ projectId, user, t }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get(`/flow/projects/${projectId}/files`).then((r) => setFiles(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/flow/projects/${projectId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("os.flow.files.added")); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error"); }
    finally { setUploading(false); }
  };

  const download = async (f) => {
    setBusy(f.id);
    try {
      const res = await api.get(`/flow/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
    finally { setBusy(null); }
  };

  const del = async (f) => {
    try { await api.delete(`/flow/files/${f.id}`); toast.success(t("os.flow.files.removed")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const fmtSize = (b) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
  const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  return (
    <div className="max-w-3xl space-y-4" data-testid="flow-files-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-navy/70">
          <Paperclip className="w-4 h-4" /> <span className="font-medium">{t("os.flow.files.title")}</span>
        </div>
        <label data-testid="flow-file-upload" className={`inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 cursor-pointer hover:opacity-90 transition-opacity ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
          {uploading ? t("os.flow.files.uploading") : t("os.flow.files.upload")}
          <input type="file" className="hidden" onChange={onPick} data-testid="flow-file-input" />
        </label>
      </div>
      <p className="text-xs text-navy/40">{t("os.flow.files.limit")}</p>

      {files.length === 0 ? (
        <div className="text-navy/40 py-8" data-testid="flow-files-empty">{t("os.flow.files.empty")}</div>
      ) : (
        <div className="space-y-2">
          {files.map((f) => (
            <motion.div key={f.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease }}
              data-testid={`flow-file-${f.id}`} className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg p-3">
              <div className="w-9 h-9 rounded-lg bg-violet/10 text-violet flex items-center justify-center shrink-0"><FileIcon className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{f.filename}</div>
                <div className="text-[11px] text-navy/40 truncate">{fmtSize(f.size)} · {f.uploader_name} · {fmtDate(f.created_at)}</div>
              </div>
              <button data-testid={`flow-file-download-${f.id}`} onClick={() => download(f)} disabled={busy === f.id}
                className="shrink-0 w-8 h-8 rounded-lg border border-navy/10 flex items-center justify-center text-navy/50 hover:text-violet hover:border-violet/40 transition-colors disabled:opacity-50">
                {busy === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              </button>
              {(user?.role === "admin" || user?.role === "ceo" || user?.role === "director" || f.uploader_id === user?.id) && (
                <button data-testid={`flow-file-del-${f.id}`} onClick={() => del(f)}
                  className="shrink-0 w-8 h-8 rounded-lg border border-navy/10 flex items-center justify-center text-navy/40 hover:text-destructive hover:border-destructive/40 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskDetailModal({ task, tasks, members, onClose, onChanged, t }) {
  const [f, setF] = useState({
    title: task.title, description: task.description || "", assignee_id: task.assignee_id || "",
    priority: task.priority || "medium", due_date: task.due_date || "",
    depends_on: task.depends_on || [],
  });
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [newItem, setNewItem] = useState("");
  const [saving, setSaving] = useState(false);
  const others = tasks.filter((x) => x.id !== task.id);

  const save = async () => {
    setSaving(true);
    const m = members.find((x) => x.id === f.assignee_id);
    try {
      await api.patch(`/flow/tasks/${task.id}`, { ...f, assignee: m ? m.name : "" });
      toast.success(t("os.flow.detail.saved")); onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); setSaving(false); }
  };
  const addItem = async () => {
    if (!newItem.trim()) return;
    const { data } = await api.post(`/flow/tasks/${task.id}/checklist`, { text: newItem.trim() });
    setChecklist((c) => [...c, data]); setNewItem("");
  };
  const toggleItem = async (it) => {
    await api.patch(`/flow/tasks/${task.id}/checklist/${it.id}`, { done: !it.done });
    setChecklist((c) => c.map((x) => (x.id === it.id ? { ...x, done: !x.done } : x)));
  };
  const delItem = async (it) => {
    await api.delete(`/flow/tasks/${task.id}/checklist/${it.id}`);
    setChecklist((c) => c.filter((x) => x.id !== it.id));
  };
  const toggleDep = (tid) => setF((s) => ({ ...s, depends_on: s.depends_on.includes(tid) ? s.depends_on.filter((x) => x !== tid) : [...s.depends_on, tid] }));
  const delTask = async () => { await api.delete(`/flow/tasks/${task.id}`); onChanged(); onClose(); };

  const done = checklist.filter((c) => c.done).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="flow-task-detail" className="w-full max-w-lg bg-cream rounded-xl p-8 max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5 gap-3">
          <input data-testid="td-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
            className="text-lg font-medium bg-transparent border-b border-transparent hover:border-navy/15 focus:border-violet outline-none flex-1 pb-1 transition-colors" />
          <button onClick={onClose} className="text-navy/40 hover:text-navy shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <textarea rows={2} data-testid="td-desc" placeholder={t("os.flow.detail.description")} value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inputCls} resize-none mb-4`} />

        <div className="grid grid-cols-2 gap-3 mb-4">
          <select data-testid="td-assignee" value={f.assignee_id} onChange={(e) => setF({ ...f, assignee_id: e.target.value })} className={inputCls}>
            <option value="">{t("os.flow.task.assigneeNone")}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className={inputCls}>
            {["low", "medium", "high"].map((v) => <option key={v} value={v}>{t(`os.flow.priority.${v}`)}</option>)}
          </select>
        </div>
        <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={`${inputCls} mb-5`} />

        {/* Checklist */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2 text-navy/70"><CheckSquare className="w-4 h-4" />
            <span className="font-medium text-sm">{t("os.flow.detail.checklist")}</span>
            {checklist.length > 0 && <span className="text-xs text-navy/40">{done}/{checklist.length}</span>}
          </div>
          <div className="space-y-1.5">
            {checklist.map((it) => (
              <div key={it.id} data-testid={`td-item-${it.id}`} className="flex items-center gap-2 group">
                <button onClick={() => toggleItem(it)} data-testid={`td-item-toggle-${it.id}`}
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${it.done ? "bg-violet border-violet text-white" : "border-navy/25"}`}>
                  {it.done && <Check className="w-3 h-3" />}
                </button>
                <span className={`text-sm flex-1 ${it.done ? "line-through text-navy/35" : "text-navy/75"}`}>{it.text}</span>
                <button onClick={() => delItem(it)} className="opacity-0 group-hover:opacity-100 text-navy/30 hover:text-destructive transition-opacity"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <input data-testid="td-item-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
              placeholder={t("os.flow.detail.addItem")} className="flex-1 bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet transition-colors" />
            <button data-testid="td-item-add" onClick={addItem} className="shrink-0 w-9 rounded-lg bg-navy/5 hover:bg-navy/10 flex items-center justify-center transition-colors"><Plus className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Dependencies */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1 text-navy/70"><Link2 className="w-4 h-4" /><span className="font-medium text-sm">{t("os.flow.detail.dependencies")}</span></div>
          <p className="text-xs text-navy/45 mb-2">{t("os.flow.detail.depsHint")}</p>
          {others.length === 0 ? <p className="text-xs text-navy/35">{t("os.flow.detail.none")}</p> : (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {others.map((o) => (
                <label key={o.id} data-testid={`td-dep-${o.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={f.depends_on.includes(o.id)} onChange={() => toggleDep(o.id)} className="accent-violet" />
                  <span className="text-navy/70">{o.title}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button data-testid="td-delete" onClick={delTask} className="rounded-full border border-destructive/30 text-destructive px-4 py-3 text-sm hover:bg-destructive/5 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
          <button data-testid="td-save" onClick={save} disabled={saving}
            className="flex-1 rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? "…" : t("os.flow.detail.save")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function InviteModal({ projectId, members, onClose, onDone, t }) {
  const [users, setUsers] = useState([]);
  const memberIds = new Set(members.map((m) => m.id));
  useEffect(() => { api.get("/flow/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const available = users.filter((u) => !memberIds.has(u.id));

  const add = async (uid) => {
    try { await api.post(`/flow/projects/${projectId}/members`, { user_id: uid }); toast.success(t("os.flow.members.added")); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="flow-invite-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium">{t("os.flow.members.inviteTitle")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {available.length === 0 ? (
          <div className="text-navy/40 py-6">{t("os.flow.members.empty")}</div>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {available.map((u) => (
              <button key={u.id} data-testid={`flow-invite-user-${u.id}`} onClick={() => add(u.id)}
                className="w-full flex items-center gap-3 bg-white border border-navy/10 rounded-lg p-3 hover:border-violet/40 transition-colors text-left">
                <Avatar name={u.name} id={u.id} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{u.name}</div>
                  <div className="text-xs text-navy/45 truncate">{u.email} · {u.role}</div>
                </div>
                <UserPlus className="w-4 h-4 text-violet shrink-0" />
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

function FlowTimeline({ tasks, memberById, t }) {
  const parsed = tasks
    .map((tk) => ({ ...tk, s: new Date(tk.start_date), e: new Date(tk.due_date) }))
    .filter((tk) => !isNaN(tk.s) && !isNaN(tk.e));
  if (!parsed.length) return <div className="text-navy/40" data-testid="flow-timeline-panel">{t("os.flow.timeline.empty")}</div>;
  let min = Math.min(...parsed.map((tk) => tk.s.getTime()));
  let max = Math.max(...parsed.map((tk) => tk.e.getTime()));
  const today = Date.now();
  min = Math.min(min, today); max = Math.max(max, today);
  const span = Math.max(max - min, 1);
  const pct = (ts) => `${((ts - min) / span) * 100}%`;
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const DONE = "bg-emerald-500", ACT = "bg-gradient-violet";
  return (
    <div className="bg-white border border-navy/10 rounded-lg p-6 md:p-8 space-y-4" data-testid="flow-timeline-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{t("os.flow.timeline.title")}</h3>
        <div className="text-xs text-navy/40">{fmt(new Date(min))} — {fmt(new Date(max))}</div>
      </div>
      <div className="relative space-y-3 pt-2">
        <div className="absolute top-0 bottom-0 w-px bg-violet/60 z-10" style={{ left: pct(today) }}>
          <span className="absolute -top-1 -translate-x-1/2 text-[9px] uppercase tracking-wider text-violet whitespace-nowrap">{t("os.flow.timeline.today")}</span>
        </div>
        {parsed.map((tk, i) => {
          const left = ((tk.s.getTime() - min) / span) * 100;
          const width = Math.max(((tk.e.getTime() - tk.s.getTime()) / span) * 100, 3);
          const asg = memberById[tk.assignee_id];
          return (
            <motion.div key={tk.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease, delay: i * 0.04 }}
              data-testid={`flow-timeline-${tk.id}`} className="grid grid-cols-[160px_1fr] items-center gap-4">
              <div className="text-xs text-navy/60 truncate">{tk.title}</div>
              <div className="relative h-6">
                <div className={`absolute top-1/2 -translate-y-1/2 h-5 rounded-md ${tk.status === "done" ? DONE : ACT} flex items-center`}
                  style={{ left: `${left}%`, width: `${width}%` }}>
                  <span className="px-2 text-[10px] text-white whitespace-nowrap truncate">{asg?.name || tk.assignee || ""}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function TaskModal({ sprints, members, onClose, onSubmit, t }) {
  const [f, setF] = useState({ title: "", assignee_id: "", assignee: "", priority: "medium", status: "backlog", sprint_id: "", start_date: "", due_date: "", start_time: "", end_time: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => { e.preventDefault(); setErr(""); try { await onSubmit(f); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); } };
  const pickAssignee = (uid) => {
    const m = members.find((x) => x.id === uid);
    setF({ ...f, assignee_id: uid, assignee: m ? m.name : "" });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="flow-task-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium">{t("os.flow.task.add")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder={t("os.flow.task.title")} data-testid="flow-input-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} />
          <select data-testid="flow-input-assignee" value={f.assignee_id} onChange={(e) => pickAssignee(e.target.value)} className={inputCls}>
            <option value="">{t("os.flow.task.assigneeNone")}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="flow-input-priority" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className={inputCls}>
              {["low", "medium", "high"].map((v) => <option key={v} value={v}>{t(`os.flow.priority.${v}`)}</option>)}
            </select>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls}>
              {COLS.map((v) => <option key={v} value={v}>{t(`os.flow.cols.${v}`)}</option>)}
            </select>
          </div>
          <select value={f.sprint_id} onChange={(e) => setF({ ...f, sprint_id: e.target.value })} className={inputCls}>
            <option value="">{t("os.flow.task.none")}</option>
            {sprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-navy/50">Inicio
              <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inputCls} /></label>
            <label className="text-xs text-navy/50">Entrega
              <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={inputCls} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-navy/50">Hora inicio
              <input type="time" data-testid="flow-task-start-time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className={inputCls} /></label>
            <label className="text-xs text-navy/50">Hora fin
              <input type="time" data-testid="flow-task-end-time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className={inputCls} /></label>
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <button data-testid="flow-task-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.flow.task.create")}</button>
        </form>
      </motion.div>
    </div>
  );
}

function SprintModal({ onClose, onSubmit, t }) {
  const [f, setF] = useState({ name: "", goal: "", start_date: "", end_date: "", status: "planned" });
  const [err, setErr] = useState("");
  const submit = async (e) => { e.preventDefault(); setErr(""); try { await onSubmit(f); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); } };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="flow-sprint-modal" className="w-full max-w-md bg-cream rounded-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium">{t("os.flow.sprint.add")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder={t("os.flow.sprint.name")} data-testid="flow-input-sprint-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
          <textarea rows={2} placeholder={t("os.flow.sprint.goal")} value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} className={`${inputCls} resize-none`} />
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inputCls} />
            <input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={inputCls} />
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <button data-testid="flow-sprint-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.flow.sprint.create")}</button>
        </form>
      </motion.div>
    </div>
  );
}
